import { useEffect, useState, type FormEvent } from 'react'
import { createPortal } from 'react-dom'
import { useTranslation } from 'react-i18next'
import QRCode from 'qrcode'
import { AdminPageShell } from '../admin/AdminPageShell'
import type { AdminSession } from '../admin/AdminShell'
import { resolveApiBaseUrl } from '../../lib/api-base-url'
import { useEscapeClose } from '../../lib/use-escape-close'

type SortKey = 'name' | 'type' | 'status' | 'updatedAt' | 'deletedAt'
type DrawerMode = 'edit'
type ModalMode = 'create' | 'view'
type PageTone = 'idle' | 'success' | 'error' | 'warning'
type MenuDirection = 'up' | 'down'
type OpenMenuState = {
  id: string
  direction: MenuDirection
  coords: {
    top: number
    left: number
    right: number
    bottom: number
  }
} | null

type SpaceItem = {
  id: string
  name: string
  type: string
  latitude: number | null
  longitude: number | null
  radiusMeters: number
  minGpsAccuracyMeters: number
  qrEnabled: boolean
  qrSamplingRate: number
  qrCode: string | null
  openingHoursEnabled: boolean
  opensAt: string | null
  closesAt: string | null
  allowedWeekdays: number[]
  outOfHoursPolicy: string
  status: string
  createdAt: string
  updatedAt: string
  deletedAt: string | null
  collaboratorsCount: number
}

type CollaboratorOption = {
  id: string
  name: string
  email: string
  role?: {
    label: string
  } | null
  status: string
}

type SpacesPayload = {
  items: SpaceItem[]
  stats: {
    total: number
    active: number
    inactive: number
    trash: number
  }
  catalog: {
    types: string[]
    statuses: string[]
    outOfHoursPolicies: string[]
  }
}

type SpaceForm = {
  name: string
  type: string
  latitude: string
  longitude: string
  radiusMeters: string
  minGpsAccuracyMeters: string
  qrEnabled: boolean
  qrSamplingRate: string
  openingHoursEnabled: boolean
  opensAt: string
  closesAt: string
  allowedWeekdays: number[]
  outOfHoursPolicy: string
  status: string
}

type SpacesPageProps = {
  session: AdminSession
}

const apiBaseUrl = resolveApiBaseUrl()
const ROWS_PER_PAGE = 10
const WEEKDAYS = [0, 1, 2, 3, 4, 5, 6]

const defaultSpaceForm: SpaceForm = {
  name: '',
  type: 'construction_site',
  latitude: '',
  longitude: '',
  radiusMeters: '100',
  minGpsAccuracyMeters: '50',
  qrEnabled: false,
  qrSamplingRate: '0',
  openingHoursEnabled: false,
  opensAt: '08:00',
  closesAt: '18:00',
  allowedWeekdays: [1, 2, 3, 4, 5],
  outOfHoursPolicy: 'reject',
  status: 'active',
}

function formatDateTime(value: string) {
  return new Intl.DateTimeFormat('pt-BR', {
    dateStyle: 'short',
    timeStyle: 'short',
  }).format(new Date(value))
}

function formatCoordinate(value: number | null) {
  if (value === null) {
    return '—'
  }

  return value.toFixed(6)
}

function SidebarPanelIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      <path d="M4 5h6v6H4z" />
      <path d="M14 5h6v6h-6z" />
      <path d="M4 13h6v6H4z" />
      <path d="M14 13h6v6h-6z" />
    </svg>
  )
}

function SearchIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      <circle cx="11" cy="11" r="6" />
      <path d="m16 16 4 4" />
    </svg>
  )
}

function PlusIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      <path d="M12 5v14" />
      <path d="M5 12h14" />
    </svg>
  )
}

function EyeIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      <path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7-10-7-10-7Z" />
      <circle cx="12" cy="12" r="3" />
    </svg>
  )
}

function PencilIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      <path d="m4 20 4.5-1 10-10-3.5-3.5-10 10Z" />
      <path d="m13 6 3.5 3.5" />
    </svg>
  )
}

function DeleteIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      <path d="M4 7h16" />
      <path d="M9 4h6" />
      <path d="M8 7l1 13h6l1-13" />
    </svg>
  )
}

function QrPrintIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      <path d="M4 4h6v6H4z" />
      <path d="M14 4h6v6h-6z" />
      <path d="M4 14h6v6H4z" />
      <path d="M14 14h2v2h-2z" />
      <path d="M18 14h2v6h-4v-2h2z" />
      <path d="M14 18h2v2h-2z" />
    </svg>
  )
}

function UsersIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      <circle cx="9" cy="8" r="3" />
      <circle cx="17" cy="9" r="2.5" />
      <path d="M4 20a5 5 0 0 1 10 0" />
      <path d="M13 20a4 4 0 0 1 7 0" />
    </svg>
  )
}

function CheckIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      <path d="m5 12 4 4 10-10" />
    </svg>
  )
}

function XIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      <path d="M6 6l12 12" />
      <path d="M18 6 6 18" />
    </svg>
  )
}

function ChevronLeftIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false" style={{ width: '13px', height: '13px', marginRight: '6px', display: 'inline-block', verticalAlign: 'middle' }}>
      <polyline points="15 18 9 12 15 6" />
    </svg>
  )
}

function ChevronRightIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false" style={{ width: '13px', height: '13px', marginLeft: '6px', display: 'inline-block', verticalAlign: 'middle' }}>
      <polyline points="9 18 15 12 9 6" />
    </svg>
  )
}

function MoreIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      <circle cx="12" cy="5" r="1.5" />
      <circle cx="12" cy="12" r="1.5" />
      <circle cx="12" cy="19" r="1.5" />
    </svg>
  )
}

function CheckboxIcon({ checked }: { checked: boolean }) {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      <rect x="4" y="4" width="16" height="16" rx="3" />
      {checked ? <path d="m7 12 3 3 7-7" /> : null}
    </svg>
  )
}

function SortIcon({ direction }: { direction: 'asc' | 'desc' }) {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      {direction === 'asc' ? <path d="m8 14 4-4 4 4" /> : <path d="m8 10 4 4 4-4" />}
    </svg>
  )
}

function statusLabel(status: string, t: (key: string) => string) {
  if (status === 'active') {
    return t('spaces.status.active')
  }

  if (status === 'inactive') {
    return t('spaces.status.inactive')
  }

  return status
}

function spaceTypeLabel(type: string, t: (key: string) => string) {
  return t(`spaces.type.${type}`)
}

function qrSamplingLabel(space: SpaceItem, t: (key: string, options?: Record<string, unknown>) => string) {
  if (space.qrSamplingRate <= 0) {
    return t('spaces.boolean.optional')
  }

  if (space.qrSamplingRate >= 100) {
    return t('spaces.boolean.required')
  }

  return t('spaces.qr.samplingLabel', { rate: space.qrSamplingRate })
}

function policyLabel(policy: string, t: (key: string) => string) {
  return t(`spaces.policy.${policy}`)
}

function createFormFromSpace(space: SpaceItem): SpaceForm {
  return {
    name: space.name,
    type: space.type,
    latitude: space.latitude === null ? '' : String(space.latitude),
    longitude: space.longitude === null ? '' : String(space.longitude),
    radiusMeters: String(space.radiusMeters),
    minGpsAccuracyMeters: String(space.minGpsAccuracyMeters),
    qrEnabled: space.qrEnabled,
    qrSamplingRate: String(space.qrSamplingRate ?? 0),
    openingHoursEnabled: space.openingHoursEnabled,
    opensAt: space.opensAt || '08:00',
    closesAt: space.closesAt || '18:00',
    allowedWeekdays: space.allowedWeekdays,
    outOfHoursPolicy: space.outOfHoursPolicy,
    status: space.status,
  }
}

function toPayload(form: SpaceForm, includeStatus: boolean) {
  return {
    name: form.name,
    type: form.type,
    latitude: form.latitude.trim() ? Number(form.latitude) : null,
    longitude: form.longitude.trim() ? Number(form.longitude) : null,
    radiusMeters: Number(form.radiusMeters),
    minGpsAccuracyMeters: Number(form.minGpsAccuracyMeters),
    qrEnabled: form.qrEnabled,
    qrSamplingRate: form.qrEnabled ? Math.min(100, Math.max(0, Number(form.qrSamplingRate) || 0)) : 0,
    openingHoursEnabled: form.openingHoursEnabled,
    opensAt: form.openingHoursEnabled ? form.opensAt : null,
    closesAt: form.openingHoursEnabled ? form.closesAt : null,
    allowedWeekdays: form.allowedWeekdays,
    outOfHoursPolicy: form.outOfHoursPolicy,
    ...(includeStatus ? { status: form.status } : {}),
  }
}

function getMenuState(button: HTMLButtonElement, id: string): OpenMenuState {
  const rect = button.getBoundingClientRect()
  const tableWrap = button.closest('.admin-table-wrap')
  let direction: MenuDirection = 'down'
  if (tableWrap) {
    const wrapRect = tableWrap.getBoundingClientRect()
    direction = wrapRect.bottom - rect.bottom < 190 ? 'up' : 'down'
  } else {
    direction = window.innerHeight - rect.bottom < 220 ? 'up' : 'down'
  }
  return {
    id,
    direction,
    coords: {
      top: rect.top,
      left: rect.left,
      right: rect.right,
      bottom: rect.bottom,
    },
  }
}

function createQrPayload(space: SpaceItem) {
  // Must match the exact string the punch API compares against
  // (space.qrCode), not a portal URL — the field app scans this
  // value and sends it verbatim to POST /activities/punch(/validate-qr).
  return space.qrCode || ''
}

function escapeHtml(value: string) {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;')
}

function createFileName(space: SpaceItem) {
  return `qrcode-${space.name
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/(^-|-$)/g, '') || space.id}.svg`
}

export function SpacesPage({ session }: SpacesPageProps) {
  const { t } = useTranslation()
  const [payload, setPayload] = useState<SpacesPayload | null>(null)
  const [loading, setLoading] = useState(true)
  const [filters, setFilters] = useState({
    search: '',
    status: '',
    type: '',
  })
  const [appliedFilters, setAppliedFilters] = useState(filters)
  const [sortBy, setSortBy] = useState<SortKey>('updatedAt')
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc')
  const [page, setPage] = useState(1)
  const [selectedIds, setSelectedIds] = useState<string[]>([])
  const [openMenu, setOpenMenu] = useState<OpenMenuState>(null)
  const [modalMode, setModalMode] = useState<ModalMode | null>(null)
  const [drawerMode, setDrawerMode] = useState<DrawerMode | null>(null)
  const [selectedSpace, setSelectedSpace] = useState<SpaceItem | null>(null)
  const [form, setForm] = useState<SpaceForm>(defaultSpaceForm)
  const [deleteTarget, setDeleteTarget] = useState<SpaceItem | null>(null)
  const [collaboratorModalSpace, setCollaboratorModalSpace] = useState<SpaceItem | null>(null)
  const [collaborators, setCollaborators] = useState<CollaboratorOption[]>([])
  const [collaboratorIds, setCollaboratorIds] = useState<string[]>([])
  const [collaboratorSearch, setCollaboratorSearch] = useState('')
  const [collaboratorsLoading, setCollaboratorsLoading] = useState(false)
  const [noticeTone, setNoticeTone] = useState<PageTone>('idle')
  const [noticeMessage, setNoticeMessage] = useState('')
  const [isSaving, setIsSaving] = useState(false)

  const items = payload?.items || []
  const totalPages = Math.max(1, Math.ceil(items.length / ROWS_PER_PAGE))
  const pagedItems = items.slice((page - 1) * ROWS_PER_PAGE, page * ROWS_PER_PAGE)
  const allPageIds = pagedItems.map((item) => item.id)
  const isAllPageSelected = allPageIds.length > 0 && allPageIds.every((id) => selectedIds.includes(id))
  const collaboratorSearchTerm = collaboratorSearch.trim().toLowerCase()
  const selectedCollaborators = collaborators.filter((collaborator) => collaboratorIds.includes(collaborator.id))
  const filteredCollaborators = collaborators
    .filter((collaborator) => !collaboratorIds.includes(collaborator.id))
    .filter((collaborator) => {
      if (!collaboratorSearchTerm) {
        return true
      }

      return [collaborator.name, collaborator.email, collaborator.role?.label || '']
        .join(' ')
        .toLowerCase()
        .includes(collaboratorSearchTerm)
    })

  const showToast = (tone: PageTone, message: string) => {
    setNoticeTone(tone)
    setNoticeMessage(message)
  }

  const apiRequest = async (path: string, init: RequestInit = {}) => {
    const response = await fetch(`${apiBaseUrl}${path}`, {
      ...init,
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${session.session.token}`,
        ...(init.headers || {}),
      },
    })

    if (!response.ok) {
      throw new Error(await response.text())
    }

    return response.status === 204 ? null : response.json()
  }

  const loadSpaces = async () => {
    const params = new URLSearchParams({
      scope: 'active',
      sortBy,
      sortDir,
    })

    if (appliedFilters.search) {
      params.set('search', appliedFilters.search)
    }

    if (appliedFilters.status) {
      params.set('status', appliedFilters.status)
    }

    if (appliedFilters.type) {
      params.set('type', appliedFilters.type)
    }

    const data = await apiRequest(`/spaces?${params.toString()}`)
    setPayload(data)
    setSelectedIds((current) => current.filter((id) => data.items.some((item: SpaceItem) => item.id === id)))
    setPage((current) => Math.min(current, Math.max(1, Math.ceil(data.items.length / ROWS_PER_PAGE))))
  }

  useEffect(() => {
    let isMounted = true

    setLoading(true)
    loadSpaces()
      .catch(() => {
        if (isMounted) {
          showToast('error', t('spaces.error.load'))
        }
      })
      .finally(() => {
        if (isMounted) {
          setLoading(false)
        }
      })

    return () => {
      isMounted = false
    }
  }, [appliedFilters, sortBy, sortDir])

  useEffect(() => {
    if (!noticeMessage) {
      return undefined
    }

    const timeout = window.setTimeout(() => {
      setNoticeMessage('')
      setNoticeTone('idle')
    }, 3200)

    return () => window.clearTimeout(timeout)
  }, [noticeMessage])

  useEffect(() => {
    const refresh = () => {
      loadSpaces().catch(() => undefined)
    }

    const interval = window.setInterval(refresh, 15000)
    window.addEventListener('focus', refresh)

    return () => {
      window.clearInterval(interval)
      window.removeEventListener('focus', refresh)
    }
  }, [appliedFilters, sortBy, sortDir])

  useEffect(() => {
    const onDocumentClick = (event: MouseEvent) => {
      const target = event.target as HTMLElement

      if (!target.closest('[data-menu-id]')) {
        setOpenMenu(null)
      }
    }

    document.addEventListener('mousedown', onDocumentClick)

    return () => {
      document.removeEventListener('mousedown', onDocumentClick)
    }
  }, [])

  const closeCollaboratorModal = () => {
    setCollaboratorModalSpace(null)
    setCollaboratorIds([])
    setCollaboratorSearch('')
  }

  useEscapeClose(Boolean(openMenu || modalMode || drawerMode || deleteTarget || collaboratorModalSpace), () => {
    if (collaboratorModalSpace) {
      closeCollaboratorModal()
      return
    }

    if (deleteTarget) {
      setDeleteTarget(null)
      return
    }

    if (modalMode) {
      setModalMode(null)
      setSelectedSpace(null)
      return
    }

    if (drawerMode) {
      setDrawerMode(null)
      setSelectedSpace(null)
      return
    }

    setOpenMenu(null)
  })

  const handleSort = (nextSortBy: SortKey) => {
    setSortBy((current) => {
      if (current === nextSortBy) {
        setSortDir((direction) => (direction === 'asc' ? 'desc' : 'asc'))
        return current
      }

      setSortDir('asc')
      return nextSortBy
    })
    setPage(1)
  }

  const handleSearch = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    setAppliedFilters(filters)
    setPage(1)
  }

  const handleClear = () => {
    const nextFilters = { search: '', status: '', type: '' }
    setFilters(nextFilters)
    setAppliedFilters(nextFilters)
    setPage(1)
  }

  const handleCreate = () => {
    setForm(defaultSpaceForm)
    setSelectedSpace(null)
    setModalMode('create')
  }

  const handleView = (space: SpaceItem) => {
    setOpenMenu(null)
    setSelectedSpace(space)
    setModalMode('view')
  }

  const handleEdit = (space: SpaceItem) => {
    setOpenMenu(null)
    setSelectedSpace(space)
    setForm(createFormFromSpace(space))
    setDrawerMode('edit')
  }

  const handleDelete = (space: SpaceItem) => {
    setOpenMenu(null)
    setDeleteTarget(space)
  }

  const handlePrintQr = async (space: SpaceItem) => {
    setOpenMenu(null)

    if (!space.qrEnabled || !space.qrCode) {
      showToast('warning', t('spaces.qr.notEnabled'))
      return
    }

    try {
      const qrPayload = createQrPayload(space)
      const qrSvg = await QRCode.toString(qrPayload, {
        type: 'svg',
        margin: 2,
        width: 320,
        errorCorrectionLevel: 'M',
        color: {
          dark: '#2b1357',
          light: '#ffffff',
        },
      })
      const qrBlob = new Blob([qrSvg], { type: 'image/svg+xml;charset=utf-8' })
      const qrUrl = URL.createObjectURL(qrBlob)
      const printWindow = window.open('', '_blank', 'width=720,height=860')

      if (!printWindow) {
        const link = document.createElement('a')
        link.href = qrUrl
        link.download = createFileName(space)
        link.click()
        URL.revokeObjectURL(qrUrl)
        showToast('warning', t('spaces.qr.downloaded'))
        return
      }

      printWindow.document.write(`
        <!doctype html>
        <html lang="pt-BR">
          <head>
            <meta charset="utf-8" />
            <title>${escapeHtml(t('spaces.qr.title'))} - ${escapeHtml(space.name)}</title>
            <style>
              :root {
                color: #2b1357;
                font-family: Georgia, 'Times New Roman', serif;
              }

              * {
                box-sizing: border-box;
              }

              body {
                min-height: 100vh;
                display: grid;
                place-items: center;
                margin: 0;
                padding: 24px;
                background: #f6f0ff;
              }

              main {
                width: min(520px, 100%);
                display: grid;
                gap: 18px;
                padding: 26px;
                border: 1px solid rgba(87, 30, 171, 0.18);
                border-radius: 5px;
                background: #ffffff;
                box-shadow: 0 22px 48px rgba(43, 19, 87, 0.14);
              }

              header {
                display: grid;
                gap: 4px;
                text-align: center;
              }

              h1 {
                margin: 0;
                font-size: 1.45rem;
              }

              p {
                margin: 0;
                color: #5f4c7c;
                line-height: 1.45;
              }

              .qr {
                display: grid;
                place-items: center;
                padding: 18px;
                border: 1px solid rgba(87, 30, 171, 0.12);
                border-radius: 5px;
              }

              .qr svg {
                width: min(320px, 100%);
                height: auto;
              }

              dl {
                display: grid;
                grid-template-columns: 1fr 1fr;
                gap: 10px;
                margin: 0;
              }

              div {
                display: grid;
                gap: 4px;
                padding: 10px;
                border: 1px solid rgba(87, 30, 171, 0.1);
                border-radius: 5px;
              }

              dt {
                color: #6d5a88;
                font-size: 0.75rem;
                text-transform: uppercase;
                letter-spacing: 0.08em;
              }

              dd {
                margin: 0;
                font-weight: 700;
                overflow-wrap: anywhere;
              }

              .actions {
                display: flex;
                gap: 10px;
                justify-content: flex-end;
              }

              button,
              a {
                display: inline-flex;
                align-items: center;
                justify-content: center;
                min-height: 38px;
                padding: 9px 12px;
                border: 1px solid rgba(87, 30, 171, 0.16);
                border-radius: 5px;
                background: #ffffff;
                color: #571eab;
                font-weight: 800;
                text-decoration: none;
                cursor: pointer;
              }

              @media print {
                body {
                  padding: 0;
                  background: #ffffff;
                }

                main {
                  box-shadow: none;
                }

                .actions {
                  display: none;
                }
              }
            </style>
          </head>
          <body>
            <main>
              <header>
                <p>${escapeHtml(t('spaces.qr.title'))}</p>
                <h1>${escapeHtml(space.name)}</h1>
              </header>
              <section class="qr">${qrSvg}</section>
              <dl>
                <div>
                  <dt>${escapeHtml(t('spaces.table.type'))}</dt>
                  <dd>${escapeHtml(spaceTypeLabel(space.type, t))}</dd>
                </div>
                <div>
                  <dt>${escapeHtml(t('spaces.table.geofence'))}</dt>
                  <dd>${space.radiusMeters}m</dd>
                </div>
                <div>
                  <dt>${escapeHtml(t('spaces.detail.location'))}</dt>
                  <dd>${escapeHtml(`${formatCoordinate(space.latitude)}, ${formatCoordinate(space.longitude)}`)}</dd>
                </div>
                <div>
                  <dt>${escapeHtml(t('spaces.qr.payload'))}</dt>
                  <dd>${escapeHtml(qrPayload)}</dd>
                </div>
              </dl>
              <nav class="actions">
                <a href="${qrUrl}" download="${escapeHtml(createFileName(space))}">${escapeHtml(t('spaces.qr.download'))}</a>
                <button type="button" onclick="window.print()">${escapeHtml(t('spaces.qr.print'))}</button>
              </nav>
            </main>
          </body>
        </html>
      `)
      printWindow.document.close()
      printWindow.focus()
      showToast('success', t('spaces.qr.ready'))
    } catch {
      showToast('error', t('spaces.qr.error'))
    }
  }

  const handleManageCollaborators = async (space: SpaceItem) => {
    setOpenMenu(null)
    setCollaboratorModalSpace(space)
    setCollaboratorSearch('')
    setCollaboratorsLoading(true)

    try {
      const [collaboratorsData, assignmentData] = await Promise.all([
        apiRequest('/collaborators?scope=active&sortBy=name&sortDir=asc'),
        apiRequest(`/spaces/${space.id}/collaborators`),
      ])

      setCollaborators(
        (collaboratorsData.items || []).filter((item: CollaboratorOption) => item.status === 'active'),
      )
      setCollaboratorIds(Array.isArray(assignmentData.collaboratorIds) ? assignmentData.collaboratorIds : [])
    } catch {
      showToast('error', t('spaces.error.collaboratorsLoad'))
      setCollaboratorModalSpace(null)
    } finally {
      setCollaboratorsLoading(false)
    }
  }

  const handleSelectCollaborator = (collaboratorId: string) => {
    if (!collaboratorId) {
      return
    }

    setCollaboratorIds((current) => (current.includes(collaboratorId) ? current : [...current, collaboratorId]))
    setCollaboratorSearch('')
  }

  const handleRemoveCollaborator = (collaboratorId: string) => {
    setCollaboratorIds((current) => current.filter((id) => id !== collaboratorId))
  }

  const handleSaveCollaborators = async () => {
    if (!collaboratorModalSpace || isSaving) {
      return
    }

    setIsSaving(true)

    try {
      const result = await apiRequest(`/spaces/${collaboratorModalSpace.id}/collaborators`, {
        method: 'POST',
        body: JSON.stringify({ collaboratorIds }),
      })
      setCollaboratorIds(result.collaboratorIds || [])
      closeCollaboratorModal()
      showToast('success', t('spaces.success.collaborators'))
      await loadSpaces()
    } catch {
      showToast('error', t('spaces.error.collaboratorsSave'))
    } finally {
      setIsSaving(false)
    }
  }

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()

    if (isSaving) {
      return
    }

    setIsSaving(true)

    try {
      if (modalMode === 'create') {
        await apiRequest('/spaces', {
          method: 'POST',
          body: JSON.stringify(toPayload(form, false)),
        })
        setModalMode(null)
        showToast('success', t('spaces.success.created'))
      }

      if (drawerMode === 'edit' && selectedSpace) {
        await apiRequest(`/spaces/${selectedSpace.id}`, {
          method: 'PATCH',
          body: JSON.stringify(toPayload(form, true)),
        })
        setDrawerMode(null)
        setSelectedSpace(null)
        showToast('success', t('spaces.success.updated'))
      }

      await loadSpaces()
    } catch {
      showToast('error', t('spaces.error.save'))
    } finally {
      setIsSaving(false)
    }
  }

  const handleConfirmDelete = async () => {
    if (!deleteTarget || isSaving) {
      return
    }

    setIsSaving(true)

    try {
      await apiRequest(`/spaces/${deleteTarget.id}`, {
        method: 'DELETE',
      })
      setDeleteTarget(null)
      showToast('success', t('spaces.success.deleted'))
      await loadSpaces()
    } catch {
      showToast('error', t('spaces.error.delete'))
    } finally {
      setIsSaving(false)
    }
  }

  const toggleSelected = (id: string) => {
    setSelectedIds((current) => (current.includes(id) ? current.filter((entry) => entry !== id) : [...current, id]))
  }

  const toggleAllPage = () => {
    setSelectedIds((current) => {
      if (isAllPageSelected) {
        return current.filter((id) => !allPageIds.includes(id))
      }

      return Array.from(new Set([...current, ...allPageIds]))
    })
  }

  const toggleWeekday = (weekday: number) => {
    setForm((current) => ({
      ...current,
      allowedWeekdays: current.allowedWeekdays.includes(weekday)
        ? current.allowedWeekdays.filter((item) => item !== weekday)
        : [...current.allowedWeekdays, weekday].sort(),
    }))
  }

  const renderSortButton = (key: SortKey, label: string) => (
    <button
      type="button"
      className={`admin-sort-button${sortBy === key ? ' admin-sort-button--active' : ''}`}
      aria-sort={sortBy === key ? (sortDir === 'asc' ? 'ascending' : 'descending') : 'none'}
      onClick={() => handleSort(key)}
    >
      <span>{label}</span>
      <SortIcon direction={sortBy === key ? sortDir : 'asc'} />
    </button>
  )

  const renderForm = (mode: 'create' | 'edit') => (
    <form className="admin-form admin-form--spaces" onSubmit={handleSubmit}>
      <label>
        <span>{t('spaces.form.name')}</span>
        <input
          type="text"
          required
          value={form.name}
          onChange={(event) => setForm((current) => ({ ...current, name: event.target.value }))}
        />
      </label>

      <label>
        <span>{t('spaces.form.type')}</span>
        <select
          required
          value={form.type}
          onChange={(event) => setForm((current) => ({ ...current, type: event.target.value }))}
        >
          {(payload?.catalog.types || [defaultSpaceForm.type]).map((type) => (
            <option key={type} value={type}>
              {spaceTypeLabel(type, t)}
            </option>
          ))}
        </select>
      </label>

      <div className="admin-form__grid">
        <label>
          <span>{t('spaces.form.latitude')}</span>
          <input
            type="number"
            step="0.000001"
            value={form.latitude}
            onChange={(event) => setForm((current) => ({ ...current, latitude: event.target.value }))}
          />
        </label>
        <label>
          <span>{t('spaces.form.longitude')}</span>
          <input
            type="number"
            step="0.000001"
            value={form.longitude}
            onChange={(event) => setForm((current) => ({ ...current, longitude: event.target.value }))}
          />
        </label>
      </div>

      <div className="admin-form__grid">
        <label>
          <span>{t('spaces.form.radiusMeters')}</span>
          <input
            type="number"
            min="1"
            required
            value={form.radiusMeters}
            onChange={(event) => setForm((current) => ({ ...current, radiusMeters: event.target.value }))}
          />
        </label>
        <label>
          <span>{t('spaces.form.minGpsAccuracyMeters')}</span>
          <input
            type="number"
            min="1"
            required
            value={form.minGpsAccuracyMeters}
            onChange={(event) => setForm((current) => ({ ...current, minGpsAccuracyMeters: event.target.value }))}
          />
        </label>
      </div>

      <div className="admin-form__grid">
        <label className="admin-form__toggle">
          <span>{t('spaces.form.qrEnabled')}</span>
          <input
            type="checkbox"
            checked={form.qrEnabled}
            onChange={(event) =>
              setForm((current) => ({
                ...current,
                qrEnabled: event.target.checked,
                qrSamplingRate: event.target.checked ? current.qrSamplingRate : '0',
              }))
            }
          />
        </label>
        <label>
          <span>{t('spaces.form.qrSamplingRate')}</span>
          <input
            type="number"
            min="0"
            max="100"
            step="5"
            disabled={!form.qrEnabled}
            value={form.qrSamplingRate}
            onChange={(event) => setForm((current) => ({ ...current, qrSamplingRate: event.target.value }))}
          />
          <small className="admin-muted">{t('spaces.form.qrSamplingRateHint')}</small>
        </label>
      </div>

      <label className="admin-form__toggle">
        <span>{t('spaces.form.openingHoursEnabled')}</span>
        <input
          type="checkbox"
          checked={form.openingHoursEnabled}
          onChange={(event) => setForm((current) => ({ ...current, openingHoursEnabled: event.target.checked }))}
        />
      </label>

      <div className="admin-form__grid">
        <label>
          <span>{t('spaces.form.opensAt')}</span>
          <input
            type="time"
            disabled={!form.openingHoursEnabled}
            value={form.opensAt}
            onChange={(event) => setForm((current) => ({ ...current, opensAt: event.target.value }))}
          />
        </label>
        <label>
          <span>{t('spaces.form.closesAt')}</span>
          <input
            type="time"
            disabled={!form.openingHoursEnabled}
            value={form.closesAt}
            onChange={(event) => setForm((current) => ({ ...current, closesAt: event.target.value }))}
          />
        </label>
      </div>

      <div className="admin-weekday-field">
        <span>{t('spaces.form.allowedWeekdays')}</span>
        <div className="admin-weekday-list">
          {WEEKDAYS.map((weekday) => (
            <button
              key={weekday}
              type="button"
              className={`admin-weekday${form.allowedWeekdays.includes(weekday) ? ' admin-weekday--active' : ''}`}
              onClick={() => toggleWeekday(weekday)}
            >
              {t(`spaces.weekday.${weekday}`)}
            </button>
          ))}
        </div>
      </div>

      <label>
        <span>{t('spaces.form.outOfHoursPolicy')}</span>
        <select
          required
          value={form.outOfHoursPolicy}
          onChange={(event) => setForm((current) => ({ ...current, outOfHoursPolicy: event.target.value }))}
        >
          {(payload?.catalog.outOfHoursPolicies || [defaultSpaceForm.outOfHoursPolicy]).map((policy) => (
            <option key={policy} value={policy}>
              {policyLabel(policy, t)}
            </option>
          ))}
        </select>
      </label>

      {mode === 'edit' ? (
        <div className="admin-status-switch" aria-label={t('spaces.form.status')}>
          <span className={`admin-status-switch__label${form.status !== 'active' ? ' admin-status-switch__label--active' : ''}`}>
            {t('spaces.status.inactive')}
          </span>
          <label className="admin-status-switch__control">
            <input
              type="checkbox"
              checked={form.status === 'active'}
              onChange={(event) =>
                setForm((current) => ({ ...current, status: event.target.checked ? 'active' : 'inactive' }))
              }
            />
            <span className="admin-status-switch__track">
              <span className="admin-status-switch__thumb" />
            </span>
          </label>
          <span className={`admin-status-switch__label${form.status === 'active' ? ' admin-status-switch__label--active' : ''}`}>
            {t('spaces.status.active')}
          </span>
        </div>
      ) : null}

      <div className="admin-drawer__actions">
        <button
          type="button"
          className="admin-link-button"
          onClick={() => {
            setModalMode(null)
            setDrawerMode(null)
          setSelectedSpace(null)
          }}
        >
          <XIcon />
          {t('spaces.actions.cancel')}
        </button>
        <button type="submit" className="admin-action-button" disabled={isSaving}>
          <CheckIcon />
          {t('spaces.actions.save')}
        </button>
      </div>
    </form>
  )

  return (
    <AdminPageShell title={t('spaces.title')} icon={<SidebarPanelIcon />}>
      <section className="admin-panel admin-panel--team admin-panel--spaces">
        <div className="admin-panel__header admin-panel__header--team">
          <div>
            <h3>{t('spaces.title')}</h3>
          </div>
          <button type="button" className="admin-action-button" onClick={handleCreate}>
            <PlusIcon />
            {t('spaces.actions.create')}
          </button>
        </div>

        <form className="admin-search" onSubmit={handleSearch}>
          <label className="admin-search__field">
            <span>{t('spaces.filters.search')}</span>
            <div className="admin-search__input">
              <SearchIcon />
              <input
                type="search"
                placeholder={t('spaces.filters.searchPlaceholder')}
                value={filters.search}
                onChange={(event) => setFilters((current) => ({ ...current, search: event.target.value }))}
              />
            </div>
          </label>
          <label className="admin-search__field">
            <span>{t('spaces.filters.type')}</span>
            <select
              value={filters.type}
              onChange={(event) => setFilters((current) => ({ ...current, type: event.target.value }))}
            >
              <option value="">{t('spaces.filters.any')}</option>
              {(payload?.catalog.types || []).map((type) => (
                <option key={type} value={type}>
                  {spaceTypeLabel(type, t)}
                </option>
              ))}
            </select>
          </label>
          <label className="admin-search__field">
            <span>{t('spaces.filters.status')}</span>
            <select
              value={filters.status}
              onChange={(event) => setFilters((current) => ({ ...current, status: event.target.value }))}
            >
              <option value="">{t('spaces.filters.any')}</option>
              {(payload?.catalog.statuses || ['active', 'inactive']).map((status) => (
                <option key={status} value={status}>
                  {statusLabel(status, t)}
                </option>
              ))}
            </select>
          </label>
          <div className="admin-search__actions">
            <button type="submit" className="admin-action-button">
              <SearchIcon />
              {t('spaces.actions.search')}
            </button>
            <button type="button" className="admin-link-button" onClick={handleClear}>
              <XIcon />
              {t('spaces.actions.clear')}
            </button>
          </div>
        </form>

        <div className="admin-table-wrap admin-table-wrap--rules">
          <table className="admin-table admin-table--rules admin-table--spaces">
            <thead>
              <tr>
                <th className="admin-table__checkbox">
                  <button type="button" className="admin-checkbox-button" aria-label={t('spaces.table.selectAll')} onClick={toggleAllPage}>
                    <CheckboxIcon checked={isAllPageSelected} />
                  </button>
                </th>
                <th>{renderSortButton('name', t('spaces.table.name'))}</th>
                <th>{renderSortButton('type', t('spaces.table.type'))}</th>
                <th>{t('spaces.table.geofence')}</th>
                <th>{t('spaces.table.qr')}</th>
                <th>{renderSortButton('status', t('spaces.table.status'))}</th>
                <th>{renderSortButton('updatedAt', t('spaces.table.updatedAt'))}</th>
                <th>{t('spaces.table.actions')}</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={8}>
                    <div className="admin-empty-state">
                      <strong>{t('spaces.states.loading')}</strong>
                    </div>
                  </td>
                </tr>
              ) : pagedItems.length === 0 ? (
                <tr>
                  <td colSpan={8}>
                    <div className="admin-empty-state">
                      <strong>{t('spaces.empty.title')}</strong>
                      <p>{t('spaces.empty.description')}</p>
                    </div>
                  </td>
                </tr>
              ) : (
                pagedItems.map((space) => (
                  <tr key={space.id}>
                    <td className="admin-table__checkbox">
                      <button
                        type="button"
                        className="admin-checkbox-button"
                        aria-label={t('spaces.table.selectRow', { name: space.name })}
                        onClick={() => toggleSelected(space.id)}
                      >
                        <CheckboxIcon checked={selectedIds.includes(space.id)} />
                      </button>
                    </td>
                    <td>
                      <strong>{space.name}</strong>
                      <small>{policyLabel(space.outOfHoursPolicy, t)}</small>
                    </td>
                    <td>{spaceTypeLabel(space.type, t)}</td>
                    <td>
                      <strong>{space.radiusMeters}m</strong>
                      <small>{formatCoordinate(space.latitude)}, {formatCoordinate(space.longitude)}</small>
                    </td>
                    <td>
                      <strong>{space.qrEnabled ? t('spaces.boolean.enabled') : t('spaces.boolean.disabled')}</strong>
                      <small>{space.qrEnabled ? qrSamplingLabel(space, t) : ''}</small>
                    </td>
                    <td>
                      <span className={`admin-status admin-status--${space.status}`}>
                        {statusLabel(space.status, t)}
                      </span>
                    </td>
                    <td>{formatDateTime(space.updatedAt)}</td>
                    <td>
                      <div className="admin-row-actions" data-menu-id={space.id}>
                        <div className="admin-row-menu">
                          <button
                            data-menu-toggle="true"
                            type="button"
                            className="admin-icon-button"
                            aria-expanded={openMenu?.id === space.id}
                            aria-haspopup="menu"
                            title={t('spaces.actions.more')}
                            onClick={(event) => {
                              const button = event.currentTarget
                              setOpenMenu((current) => (current?.id === space.id ? null : getMenuState(button, space.id)))
                            }}
                          >
                            <MoreIcon />
</button>
                          {openMenu?.id === space.id ? createPortal(
                            <div
                              data-menu-id={openMenu.id}
                              className={`admin-row-menu__panel admin-row-menu__panel--${openMenu.direction}`}
                              role="menu"
                              style={{
                                position: 'fixed',
                                top: openMenu.direction === 'down' ? `${openMenu.coords.bottom + 8}px` : 'auto',
                                bottom: openMenu.direction === 'up' ? `${window.innerHeight - openMenu.coords.top + 8}px` : 'auto',
                                left: openMenu.coords.left < window.innerWidth / 2 ? `${openMenu.coords.left}px` : 'auto',
                                right: openMenu.coords.left < window.innerWidth / 2 ? 'auto' : `${window.innerWidth - openMenu.coords.right}px`,
                                transform: 'none',
                                zIndex: 9999,
                              }}
                            >
                              <button type="button" role="menuitem" className="admin-row-menu__item" onClick={() => handleView(space)}>
                                <EyeIcon />
                                {t('spaces.actions.view')}
                              </button>
                              <button type="button" role="menuitem" className="admin-row-menu__item" onClick={() => handleEdit(space)}>
                                <PencilIcon />
                                {t('spaces.actions.edit')}
                              </button>
                              <button type="button" role="menuitem" className="admin-row-menu__item" onClick={() => handlePrintQr(space)}>
                                <QrPrintIcon />
                                {t('spaces.actions.qr')}
                              </button>
                              <button type="button" role="menuitem" className="admin-row-menu__item" onClick={() => handleManageCollaborators(space)}>
                                <UsersIcon />
                                {t('spaces.actions.collaborators')}
                              </button>
                              <button type="button" role="menuitem" className="admin-row-menu__item admin-row-menu__item--danger" onClick={() => handleDelete(space)}>
                                <DeleteIcon />
                                {t('spaces.actions.delete')}
                              </button>
                            </div>,
                            document.body
                          ) : null}
                        </div>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        <div className="admin-pagination" aria-label={t('spaces.pagination.label')}>
          <div className="admin-pagination__summary">
            <span>{t('spaces.pagination.page', { page, pages: totalPages })}</span>
            <strong>{t('spaces.pagination.items', { count: items.length, total: payload?.stats.total || 0 })}</strong>
          </div>
          <div className="admin-pagination__nav">
            <div className="admin-pagination__actions">
              <button type="button" className="admin-link-button" disabled={page === 1} onClick={() => setPage((current) => Math.max(1, current - 1))}>
                <ChevronLeftIcon />
                {t('spaces.pagination.previous')}
              </button>
            </div>
            <div className="admin-pagination__pages" aria-label={t('spaces.pagination.label')}>
              {Array.from({ length: totalPages }).map((_, index) => (
                <button
                  key={index + 1}
                  type="button"
                  className={`admin-pagination__page${page === index + 1 ? ' admin-pagination__page--active' : ''}`}
                  aria-current={page === index + 1 ? 'page' : undefined}
                  onClick={() => setPage(index + 1)}
                >
                  {index + 1}
                </button>
              ))}
            </div>
            <div className="admin-pagination__actions admin-pagination__actions--end">
              <button type="button" className="admin-link-button" disabled={page === totalPages} onClick={() => setPage((current) => Math.min(totalPages, current + 1))}>
                {t('spaces.pagination.next')}
                <ChevronRightIcon />
              </button>
            </div>
          </div>
        </div>
      </section>

      {modalMode === 'create' ? (
        <div className="admin-modal" role="dialog" aria-modal="true" aria-label={t('spaces.drawer.newTitle')}>
          <div className="admin-modal__surface">
            <button
              type="button"
              className="admin-icon-button admin-icon-button--bare admin-modal__close"
              aria-label={t('spaces.actions.close')}
              onClick={() => setModalMode(null)}
            >
              <XIcon />
            </button>
            <div className="admin-drawer__header">
              <div>
                <p className="admin-modal__eyebrow">{t('spaces.drawer.newTitle')}</p>
              </div>
            </div>
            {renderForm('create')}
          </div>
        </div>
      ) : null}

      {modalMode === 'view' && selectedSpace ? (
        <div className="admin-modal" role="dialog" aria-modal="true" aria-label={t('spaces.drawer.viewTitle')}>
          <div className="admin-modal__surface admin-modal__surface--view">
            <button
              type="button"
              className="admin-icon-button admin-icon-button--bare admin-modal__close"
              aria-label={t('spaces.actions.close')}
              onClick={() => {
                setModalMode(null)
                setSelectedSpace(null)
              }}
            >
              <XIcon />
            </button>
            <div className="admin-modal__view">
              <div className="admin-modal__view-hero">
                <p className="admin-modal__eyebrow">{t('spaces.drawer.viewTitle')}</p>
                <h3>{selectedSpace.name}</h3>
              </div>
              <div className="admin-modal__view-grid">
                <div className="admin-view-card">
                  <span>{t('spaces.table.type')}</span>
                  <strong>{spaceTypeLabel(selectedSpace.type, t)}</strong>
                </div>
                <div className="admin-view-card">
                  <span>{t('spaces.table.status')}</span>
                  <strong>{statusLabel(selectedSpace.status, t)}</strong>
                </div>
                <div className="admin-view-card">
                  <span>{t('spaces.detail.location')}</span>
                  <strong>{formatCoordinate(selectedSpace.latitude)}, {formatCoordinate(selectedSpace.longitude)}</strong>
                </div>
                <div className="admin-view-card">
                  <span>{t('spaces.form.radiusMeters')}</span>
                  <strong>{selectedSpace.radiusMeters}m</strong>
                </div>
                <div className="admin-view-card">
                  <span>{t('spaces.table.qr')}</span>
                  <strong>{selectedSpace.qrEnabled ? t('spaces.boolean.enabled') : t('spaces.boolean.disabled')}</strong>
                </div>
                {selectedSpace.qrEnabled && (
                  <div className="admin-view-card">
                    <span>{t('spaces.form.qrSamplingRate')}</span>
                    <strong>{qrSamplingLabel(selectedSpace, t)}</strong>
                  </div>
                )}
                <div className="admin-view-card">
                  <span>{t('spaces.detail.collaborators')}</span>
                  <strong>{selectedSpace.collaboratorsCount}</strong>
                </div>
              </div>
              <div className="admin-modal__actions admin-modal__actions--view">
                <button
                  type="button"
                  className="admin-link-button"
                  onClick={() => {
                    setModalMode(null)
                    setSelectedSpace(null)
                  }}
                >
                  <XIcon />
                  {t('spaces.actions.cancel')}
                </button>
              </div>
            </div>
          </div>
        </div>
      ) : null}

      {drawerMode === 'edit' ? (
        <div className="admin-drawer" role="dialog" aria-modal="true" aria-label={t('spaces.drawer.editTitle')}>
          <div className="admin-drawer__surface admin-drawer__surface--topform">
            <div className="admin-drawer__header">
              <div>
                <p className="admin-drawer__eyebrow">{t('spaces.drawer.editTitle')}</p>
              </div>
              <button
                type="button"
                className="admin-icon-button admin-icon-button--bare"
                aria-label={t('spaces.actions.close')}
                onClick={() => {
                  setDrawerMode(null)
                  setSelectedSpace(null)
                }}
              >
                <XIcon />
              </button>
            </div>
            {renderForm('edit')}
          </div>
        </div>
      ) : null}

      {deleteTarget ? (
        <div className="admin-modal" role="dialog" aria-modal="true" aria-label={t('spaces.modal.deleteTitle')}>
          <div className="admin-modal__surface admin-modal__surface--confirm">
            <button
              type="button"
              className="admin-icon-button admin-icon-button--bare admin-modal__close"
              aria-label={t('spaces.actions.close')}
              onClick={() => setDeleteTarget(null)}
            >
              <XIcon />
            </button>
            <p className="admin-modal__eyebrow">{t('spaces.modal.deleteTitle')}</p>
            <p>{t('spaces.modal.deleteText')}</p>
            <div className="admin-modal__actions">
              <button type="button" className="admin-link-button" onClick={() => setDeleteTarget(null)}>
                <XIcon />
                {t('spaces.actions.cancel')}
              </button>
              <button type="button" className="admin-action-button admin-action-button--danger" onClick={handleConfirmDelete} disabled={isSaving}>
                <DeleteIcon />
                {t('spaces.actions.delete')}
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {collaboratorModalSpace ? (
        <div
          className="admin-modal"
          role="dialog"
          aria-modal="true"
          aria-labelledby="space-collaborators-title"
        >
          <div className="admin-modal__surface admin-modal__surface--view admin-modal__surface--space-collaborators">
            <button
              type="button"
              className="admin-icon-button admin-icon-button--bare admin-modal__close"
              aria-label={t('spaces.actions.close')}
              onClick={closeCollaboratorModal}
            >
              <XIcon />
            </button>
            <div className="admin-modal__view">
              <div className="admin-modal__view-hero">
                <p className="admin-modal__eyebrow">{t('spaces.collaborators.title')}</p>
                <h3 id="space-collaborators-title">{collaboratorModalSpace.name}</h3>
              </div>

              <div className="admin-space-collaborator-picker">
                <label className="admin-space-collaborator-field">
                  <span>{t('spaces.collaborators.search')}</span>
                  <div className="admin-search__input">
                    <SearchIcon />
                    <input
                      type="search"
                      placeholder={t('spaces.collaborators.searchPlaceholder')}
                      value={collaboratorSearch}
                      onChange={(event) => setCollaboratorSearch(event.target.value)}
                      disabled={collaboratorsLoading || collaborators.length === 0}
                    />
                  </div>
                </label>

                <label className="admin-space-collaborator-field">
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span>{t('spaces.collaborators.select')}</span>
                    {filteredCollaborators.length > 0 && (
                      <button
                        type="button"
                        className="admin-link-button"
                        style={{ padding: '0', fontSize: '0.72rem', border: '0', background: 'transparent', height: 'auto', textTransform: 'none', fontWeight: 700, letterSpacing: 'normal', color: 'var(--brand-purple)', cursor: 'pointer' }}
                        onClick={() => setCollaboratorIds(collaborators.map((c) => c.id))}
                      >
                        {t('spaces.collaborators.selectAll')}
                      </button>
                    )}
                  </div>
                  <select
                    value=""
                    onChange={(event) => handleSelectCollaborator(event.target.value)}
                    disabled={collaboratorsLoading || filteredCollaborators.length === 0}
                  >
                    <option value="">
                      {filteredCollaborators.length === 0
                        ? t('spaces.collaborators.noResults')
                        : t('spaces.collaborators.selectPlaceholder')}
                    </option>
                    {filteredCollaborators.map((collaborator) => (
                      <option key={collaborator.id} value={collaborator.id}>
                        {collaborator.name} - {collaborator.email}
                      </option>
                    ))}
                  </select>
                </label>
              </div>

              <div className="admin-space-collaborator-selected">
                <div className="admin-space-collaborator-selected__header">
                  <span>{t('spaces.collaborators.selected')}</span>
                  <strong aria-label={t('spaces.collaborators.selectedCount', { count: selectedCollaborators.length })}>
                    {selectedCollaborators.length}
                  </strong>
                </div>

                {collaboratorsLoading ? (
                  <div className="admin-empty-state">
                    <strong>{t('spaces.states.loading')}</strong>
                  </div>
                ) : collaborators.length === 0 ? (
                  <div className="admin-empty-state">
                    <strong>{t('spaces.collaborators.empty')}</strong>
                  </div>
                ) : selectedCollaborators.length === 0 ? (
                  <div className="admin-empty-state">
                    <strong>{t('spaces.collaborators.noneSelected')}</strong>
                  </div>
                ) : (
                  <div className="admin-space-collaborators">
                    {selectedCollaborators.map((collaborator) => (
                      <div key={collaborator.id} className="admin-space-collaborator">
                        <span>
                          <strong>{collaborator.name}</strong>
                          <small>{collaborator.email}</small>
                        </span>
                        {collaborator.role?.label ? <em>{collaborator.role.label}</em> : null}
                        <button
                          type="button"
                          className="admin-icon-button admin-icon-button--bare"
                          aria-label={t('spaces.collaborators.remove')}
                          onClick={() => handleRemoveCollaborator(collaborator.id)}
                        >
                          <XIcon />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className="admin-modal__actions admin-modal__actions--view">
                <button type="button" className="admin-link-button" onClick={closeCollaboratorModal}>
                  <XIcon />
                  {t('spaces.actions.cancel')}
                </button>
                <button type="button" className="admin-action-button" onClick={handleSaveCollaborators} disabled={isSaving}>
                  <CheckIcon />
                  {t('spaces.actions.save')}
                </button>
              </div>
            </div>
          </div>
        </div>
      ) : null}

      {noticeMessage ? (
        <div className="admin-toast-stack" role="status" aria-live="polite">
          <div className={`admin-toast admin-toast--${noticeTone}`}>
            <div className="admin-toast__content">
              <strong>{noticeMessage}</strong>
            </div>
          </div>
        </div>
      ) : null}
    </AdminPageShell>
  )
}
